import { useState } from 'react';

import { useClickhouseMediaAnalytics } from '@/utils/hooks/featureFlag/useClickhouseMediaAnalytics';
//hooks
import { useTransformStringOnOffset } from '@/utils/hooks/format/useTransformString';
import useTranslation from '@/utils/hooks/translation/useTranslation';

import { classNames } from '@/utils';
import useToggle from '@/utils/hooks/utils/useToggle';

import PropTypes from 'prop-types';

import { LI_POST_TYPES } from '@/lib/posts/postTypes';
import { POST } from '@/lib/posts/postTypes';
// Lib
import { LINKEDIN } from '@/lib/socialProfile';

// Components
import Carousel from '@/components/base/Carousel';
import Icon from '@/components/base/Icon';
import { Img } from '@/components/composite/MediaViewer/PostPreview/Img';
import { Item } from '@/components/composite/MediaViewer/PostPreview/Item';

export const PreviewLinkedin = ({ post, isLoading, ...restProps }) => {
    const { t } = useTranslation('commons');
    const [videoPlayedId, setVideoPlayedId] = useState(null);

    if (isLoading) {
        return (
            <span className="square block animate-pulse rounded-md bg-icoGray-300 dark:bg-icoDarkMode-deepBlack" />
        );
    }

    return (
        <div
            className="group relative flex min-h-[400px] w-full flex-col overflow-hidden rounded-md bg-icoGray-100 p-16 dark:bg-icoDarkMode-deepBlack"
            aria-label="preview-linkedin">
            <Status
                post={post}
                className={classNames('text-sm', {
                    'mb-8': post?.type !== LI_POST_TYPES.TYPE_STATUS
                })}
            />
            {post?.type === LI_POST_TYPES.TYPE_DOCUMENT && (
                <>
                    {post?.medias[0]?.pdf ? (
                        <div className="square relative w-full overflow-hidden rounded-t-md border border-icoGray-300">
                            <Img
                                postId={post?.id}
                                mediaId={post?.id}
                                src={post?.medias[0]?.thumbnail}
                            />
                        </div>
                    ) : post?.medias[0]?.thumbnail ? (
                        <div className="square relative w-full overflow-hidden rounded-t-md border border-icoGray-300">
                            <Img
                                postId={post?.id}
                                mediaId={post?.id}
                                src={post?.medias[0]?.thumbnail}
                            />
                        </div>
                    ) : null}
                    {post?.medias[0]?.link ||
                    post?.medias[0]?.title ||
                    post?.medias[0]?.pages?.length ? (
                        <div
                            className={classNames(
                                'flex flex-col space-y-1 border border-icoGray-300 bg-white p-5 text-xxs',
                                {
                                    'rounded-b-md border-t-0': post?.medias[0]?.thumbnail,
                                    'rounded-md': !post?.medias[0]?.thumbnail
                                }
                            )}
                            aria-label="linkedin-document">
                            {post?.medias[0]?.title && <p>{post?.medias[0]?.title}</p>}
                            {post?.medias[0]?.pages && <p>{post?.medias[0]?.pages} pages</p>}
                            {post?.medias[0]?.link && (
                                <a
                                    href={post?.medias[0]?.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex max-w-max space-x-1 text-xxs text-icoGray-500 dark:text-icoDarkMode-wolf">
                                    <Icon icon="export" /> <span>{t('download')}</span>{' '}
                                    <span>{post?.medias[0]?.document_type}</span>
                                </a>
                            )}
                        </div>
                    ) : null}
                </>
            )}
            {post?.type === LI_POST_TYPES.TYPE_LINK && (
                <a
                    href={post?.medias[0]?.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between rounded border border-icoGray-300 bg-white p-5 dark:border-icoDarkMode-beton dark:bg-icoDarkMode-anthracite"
                    aria-label="linkedin-link">
                    <div className="flex flex-col space-y-1">
                        <span className="text-xxs">{post?.medias[0]?.text}</span>
                        <p className="flex items-center text-icoGray-500 dark:text-icoDarkMode-wolf">
                            <Icon icon="link" className="text-xxs" />
                            <span className="ml-2 text-xxs">{post?.medias[0]?.link}</span>
                        </p>
                    </div>
                    <Icon
                        icon="chevron-right"
                        className="text-xs text-icoGray-500 dark:text-icoDarkMode-wolf"
                    />
                </a>
            )}
            {(post?.type === LI_POST_TYPES.TYPE_PHOTO ||
                post?.type === LI_POST_TYPES.TYPE_VIDEO) && (
                <Item
                    post={post}
                    postType={POST}
                    mediaId={0}
                    media={post?.medias[0]}
                    videoPlayedId={videoPlayedId}
                    setVideoPlayedId={setVideoPlayedId}
                    config={{ withAltText: false, imgFit: 'cover' }}
                    aria-label="linkedin-media"
                    network={LINKEDIN}
                    {...restProps}
                />
            )}

            {post?.type === LI_POST_TYPES.TYPE_CAROUSEL && (
                <Carousel
                    config={{ slidesToScroll: 1 }}
                    hasDots
                    navPosition="external"
                    dotVariant="darken"
                    navOnHover={true}
                    navCallback={() => {
                        if (videoPlayedId !== null) {
                            setVideoPlayedId(null);
                        }
                    }}
                    aria-label="linkedin-carousel">
                    {post?.medias?.map((media, index) => {
                        return (
                            <Carousel.Slide key={index} size="full">
                                <Item
                                    post={post}
                                    postType={POST}
                                    mediaId={index}
                                    media={media}
                                    videoPlayedId={videoPlayedId}
                                    setVideoPlayedId={setVideoPlayedId}
                                    config={{ withAltText: false, imgFit: 'cover' }}
                                    network={LINKEDIN}
                                    {...restProps}
                                />
                            </Carousel.Slide>
                        );
                    })}
                </Carousel>
            )}
        </div>
    );
};

PreviewLinkedin.propTypes = {
    post: PropTypes.object,
    isLoading: PropTypes.bool,
    className: PropTypes.string
};

const Status = ({ post, className }) => {
    const { t } = useTranslation('commons');
    const { isOpen: open, toggle } = useToggle();
    const { isActiveForNetwork } = useClickhouseMediaAnalytics();
    const isV2 = isActiveForNetwork(LINKEDIN);
    const { OffsetReplace, formatTextWithComponent } = useTransformStringOnOffset();

    const getFormattedCaption = (caption, tags) => {
        if (!caption) return null;
        return formatTextWithComponent(caption, tags);
    };

    const rawCaption = isV2 ? post?.caption : post?.caption_formatted;
    const caption = isV2 ? getFormattedCaption(rawCaption, post?.tags) : rawCaption;

    if (!caption) return null;
    if (!open) {
        return (
            <>
                <div className={classNames('line-clamp-2', className)}>
                    <OffsetReplace
                        text={caption}
                        components={Object.assign(
                            { p: <p className="inline" />, br: <br /> },
                            post?.tags?.map((tag, id) => (
                                // eslint-disable-next-line jsx-a11y/anchor-has-content
                                <a
                                    key={id}
                                    href={tag.link}
                                    className="text-icoBlue"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                />
                            ))
                        )}
                    />
                </div>

                <button
                    className="mb-1 inline-flex max-w-max items-center space-x-0.5 text-sm text-icoBlue"
                    onClick={toggle}>
                    <span>{t('view_more')}</span>
                    <Icon icon="chevron-bottom" className="relative top-px text-xs" />
                </button>
            </>
        );
    }
    return (
        <>
            <div className={className}>
                <OffsetReplace
                    text={caption}
                    components={Object.assign(
                        { p: <p />, br: <br /> },
                        post?.tags?.map((tag, id) => (
                            // eslint-disable-next-line jsx-a11y/anchor-has-content
                            <a
                                key={id}
                                href={tag.link}
                                className="text-icoBlue"
                                target="_blank"
                                rel="noopener noreferrer"
                            />
                        ))
                    )}
                />
            </div>
            <button
                className="mb-1 inline-flex max-w-max items-center space-x-0.5 text-sm text-icoBlue"
                onClick={toggle}>
                <span>{t('view_less')}</span>
                <Icon icon="chevron-top" className="relative top-px text-xs" />
            </button>
        </>
    );
};

Status.propTypes = {
    post: PropTypes.object,
    className: PropTypes.string
};

export default Status;
